import React, { useState, useEffect } from 'react';
import { X, Download, ExternalLink, ZoomIn, ZoomOut, FileText, Image as ImageIcon, File, Loader2 } from 'lucide-react';

export interface FilePreviewItem {
  url: string;
  name: string;
  size?: number;
  type?: 'image' | 'pdf' | 'doc' | 'file';
}

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: FilePreviewItem | null;
}

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  isOpen,
  onClose,
  file,
}) => {
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setZoom(1);
    setLoading(true);
  }, [file]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !file) return null;

  const getFileType = (): 'image' | 'pdf' | 'doc' | 'file' => {
    if (file.type) return file.type;
    const ext = file.name.toLowerCase().split('.').pop() || '';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
      return 'image';
    }
    if (ext === 'pdf') {
      return 'pdf';
    }
    if (['doc', 'docx', 'txt', 'rtf', 'odt', 'csv', 'xls', 'xlsx'].includes(ext)) {
      return 'doc';
    }
    return 'file';
  };

  const fileType = getFileType();

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));

  // Determine secure direct view URL (if url doesn't already route through view endpoint)
  const viewUrl = file.url.includes('/api/files/view')
    ? file.url
    : `/api/files/view?url=${encodeURIComponent(file.url)}&filename=${encodeURIComponent(file.name)}`;

  const downloadUrl = file.url.includes('/api/files/view')
    ? (file.url.includes('?') ? `${file.url}&download=true` : `${file.url}?download=true`)
    : `/api/files/view?url=${encodeURIComponent(file.url)}&filename=${encodeURIComponent(file.name)}&download=true`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50/80">
          <div className="flex items-center space-x-3 truncate mr-4">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              {fileType === 'image' && <ImageIcon className="w-5 h-5" />}
              {fileType === 'pdf' && <FileText className="w-5 h-5 text-red-500" />}
              {(fileType === 'doc' || fileType === 'file') && <File className="w-5 h-5 text-gray-600" />}
            </div>
            <div className="truncate">
              <h3 className="text-base font-semibold text-gray-900 truncate" title={file.name}>
                {file.name}
              </h3>
              {file.size && (
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center space-x-2">
            {fileType === 'image' && (
              <div className="hidden sm:flex items-center bg-white border border-gray-200 rounded-lg px-2 py-1 mr-2 shadow-sm">
                <button
                  onClick={handleZoomOut}
                  className="p-1 text-gray-500 hover:text-gray-900 transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-gray-600 px-2">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  className="p-1 text-gray-500 hover:text-gray-900 transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            )}

            <a
              href={viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-5 h-5" />
            </a>

            <a
              href={downloadUrl}
              download={file.name}
              className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
              title="Download file"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
            </a>

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors ml-2"
              title="Close (Esc)"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content Viewer */}
        <div className="flex-1 bg-gray-900/5 relative overflow-auto flex items-center justify-center p-4">
          {fileType === 'image' && (
            <div className="relative max-w-full max-h-full flex items-center justify-center overflow-auto">
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              )}
              <img
                src={viewUrl}
                alt={file.name}
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                className={`max-w-full max-h-[70vh] object-contain rounded-lg shadow-md transition-transform duration-100 ${
                  loading ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={() => setLoading(false)}
                onError={() => setLoading(false)}
              />
            </div>
          )}

          {fileType === 'pdf' && (
            <div className="w-full h-full rounded-lg overflow-hidden bg-white shadow-inner flex flex-col">
              <iframe
                src={`${viewUrl}#toolbar=1`}
                title={file.name}
                className="w-full h-full border-0"
              />
            </div>
          )}

          {(fileType === 'doc' || fileType === 'file') && (
            <div className="text-center p-8 max-w-md bg-white rounded-2xl shadow-md border border-gray-200">
              <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                <FileText className="w-10 h-10" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-1 truncate" title={file.name}>
                {file.name}
              </h4>
              {file.size && (
                <p className="text-sm text-gray-500 mb-6">{formatFileSize(file.size)}</p>
              )}
              <p className="text-sm text-gray-600 mb-6">
                This document type cannot be rendered inline in the browser preview. You can open or download it to view.
              </p>
              <div className="flex items-center justify-center space-x-3">
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg text-sm transition-colors flex items-center space-x-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Open in Tab</span>
                </a>
                <a
                  href={downloadUrl}
                  download={file.name}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors flex items-center space-x-2 shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
